import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Download, Plus, Receipt } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { PageTransition } from '../components/PageTransition';
import { useNotification } from '../contexts/NotificationContext';
import { useApprovalCounts } from '../contexts/ApprovalCountsContext';
import {
  CreateExpenseClaimDto,
  ExpenseClaim,
  expenseClaimsService,
} from '../services/expenseClaimsService';
import { ExpenseClaimDialog } from '../components/ExpenseClaimDialog';
import { ExpenseClaimsList } from '../components/ExpenseClaimsList';

type ExpenseClaimsLocationState = {
  from?: string;
  returnTo?: string;
};

const ExpenseClaims: React.FC = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { refreshCounts } = useApprovalCounts();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state || {}) as ExpenseClaimsLocationState;
  const canBackToTimesheet = locationState.from === 'monthly-timesheet';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myClaims, setMyClaims] = useState<ExpenseClaim[]>([]);
  const [detailClaim, setDetailClaim] = useState<ExpenseClaim | null>(null);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const mine = await expenseClaimsService.getMine();
      setMyClaims(mine);
    } catch (error) {
      console.error('Failed to fetch expense claims:', error);
      showNotification(
        t('expenseClaims.messages.fetchError', 'Failed to load expense claims'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [showNotification, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (
    payloads: CreateExpenseClaimDto | CreateExpenseClaimDto[],
    submit: boolean,
  ) => {
    try {
      setSaving(true);
      const items = Array.isArray(payloads) ? payloads : [payloads];
      await Promise.all(
        items.map((p) =>
          submit
            ? expenseClaimsService.createAndSubmit(p)
            : expenseClaimsService.create(p),
        ),
      );
      showNotification(
        submit
          ? items.length > 1
            ? t('expenseClaims.messages.submittedMultiple', '{{count}} note spese inviate', { count: items.length })
            : t('expenseClaims.messages.submitted', 'Nota spese inviata')
          : items.length > 1
            ? t('expenseClaims.messages.createdMultiple', '{{count}} note spese create', { count: items.length })
            : t('expenseClaims.messages.created', 'Nota spese creata'),
        'success',
      );
      setClaimDialogOpen(false);
      await fetchData();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to save expense claim:', error);
      showNotification(
        t('expenseClaims.messages.saveError', 'Failed to save expense claim'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (claim: ExpenseClaim) => {
    try {
      await expenseClaimsService.submit(claim.id);
      showNotification(
        t('expenseClaims.messages.submitted', 'Expense claim submitted'),
        'success',
      );
      await fetchData();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to submit expense claim:', error);
      showNotification(
        t('expenseClaims.messages.submitError', 'Failed to submit expense claim'),
        'error',
      );
    }
  };

  const handleDelete = async (claim: ExpenseClaim) => {
    try {
      await expenseClaimsService.remove(claim.id);
      showNotification(
        t('expenseClaims.messages.deleted', 'Expense claim deleted'),
        'success',
      );
      await fetchData();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to delete expense claim:', error);
      showNotification(
        t('expenseClaims.messages.deleteError', 'Failed to delete expense claim'),
        'error',
      );
    }
  };

  return (
    <PageTransition className="p-4 sm:p-6 space-y-6">
      <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <Receipt className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('expenseClaims.title', 'Note Spese')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(
                'expenseClaims.subtitle',
                'Crea, invia e monitora le spese rimborsabili.',
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setClaimDialogOpen(true);
            }}
            className="w-full gap-2 bg-orange-500 hover:bg-orange-600 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            {t('expenseClaims.form.newTitle', 'Aggiungi nota spese')}
          </Button>
        </div>
      </div>

      {canBackToTimesheet && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(locationState.returnTo || '/timesheet')}
            className="gap-1"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {t('menu.monthlyView', 'Vista mensile')}
          </Button>
          <span>/</span>
          <span className="font-medium text-foreground">{t('expenseClaims.title', 'Note Spese')}</span>
        </div>
      )}
      </div>

      <ExpenseClaimDialog
        open={claimDialogOpen}
        editingClaim={null}
        loading={saving}
        onSave={handleSave}
        onClose={() => {
          setClaimDialogOpen(false);
        }}
      />

      <ExpenseClaimsList
        claims={myClaims}
        loading={loading}
        withoutContainer
        onViewDetails={(claim) => setDetailClaim(claim)}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <Dialog open={!!detailClaim} onOpenChange={(open) => !open && setDetailClaim(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {t('expenseClaims.approvedHistory.detailTitle', 'Dettaglio nota spese #{{id}}', {
                id: detailClaim?.id,
              })}
            </DialogTitle>
            <DialogDescription>
              {detailClaim
                ? `${detailClaim.date.slice(0, 10)} - EUR ${Number(detailClaim.amount).toFixed(2)}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {detailClaim && (
            <div className="space-y-3">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p><strong>{t('expenseClaims.form.category', 'Categoria')}:</strong> {t(`expenseClaims.categories.${detailClaim.category.toLowerCase()}`, detailClaim.category)}</p>
                <p><strong>{t('common.status', 'Stato')}:</strong> {t(`expenseClaims.status.${detailClaim.status.toLowerCase()}`, detailClaim.status)}</p>
                <p><strong>{t('expenseClaims.list.reasonLabel', 'Motivazione')}:</strong> {detailClaim.notes || '-'}</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('expenseClaims.form.attachments', 'Allegati')}</p>
                {(detailClaim.attachments?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('expenseClaims.approvedHistory.noAttachments', 'Nessun allegato disponibile.')}</p>
                ) : (
                  detailClaim.attachments.map((attachment) => (
                    <Button key={attachment.id || attachment.fileUrl} type="button" variant="outline" className="w-full justify-center" asChild>
                      <a href={attachment.fileUrl} target="_blank" rel="noreferrer" className="inline-flex w-full items-center justify-center gap-1">
                        <Download className="h-4 w-4" />
                        {t('common.download', 'Scarica')}
                      </a>
                    </Button>
                  ))
                )}
              </div>
            </div>
          )}

          <DialogFooter className="justify-start">
            <Button type="button" variant="outline" onClick={() => setDetailClaim(null)}>
              {t('common.close', 'Chiudi')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!loading && myClaims.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('expenseClaims.empty', 'No expense claims yet.')}
          </CardContent>
        </Card>
      )}
    </PageTransition>
  );
};

export default ExpenseClaims;
